// ── Design decisions ──
// Parser PURO de listas en Excel (.xlsx) para la agencia. Produce la misma
// forma { extracted: { paquetes } } que la Edge Function y parseListaTexto,
// así el drawer de revisión no sabe de dónde vino cada grupo.
//
// · xlsx-js-style (dependencia ya existente) se usa SOLO para abrir el libro
//   (hojasDesdeWorkbook / hojasDesdeArrayBuffer). Todo lo demás trabaja sobre
//   string[][] para que las pruebas no necesiten binarios.
// · Celdas combinadas: el valor de la esquina superior izquierda se copia a
//   todo el rango, así un encabezado combinado ("Artículo" sobre dos columnas)
//   se detecta igual que uno normal.
// · Detección de encabezados insensible a acentos y mayúsculas, con sinónimos
//   salvadoreños (Cant., Artículo, Valor, N/U…). Tolera filas de título arriba:
//   el encabezado es la PRIMERA fila con ≥2 columnas reconocidas.
// · La única columna obligatoria es la descripción. Si no se identifica, el
//   resultado pide el paso de mapeo (necesitaMapeo + encabezados + muestra) en
//   vez de adivinar.
// · Agrupación: con columna de destinatario, las filas consecutivas con el
//   mismo nombre forman un grupo (una celda vacía hereda el grupo). Sin esa
//   columna, una fila en blanco o una fila "solo nombre" abre grupo nuevo.
// · Cero descartes silenciosos: toda fila que no se pueda clasificar termina
//   como artículo con requires_review y una nota. Una fila de título antes
//   del encabezado se anota en extraction_notes.
// · unit_price null cuando la celda está vacía: el mapeador lo convierte en
//   pending_review — acá nunca se inventa un precio.

import XLSX from 'xlsx-js-style';

const normalizar = (s) =>
  String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const CAMPOS = ['destinatario', 'cantidad', 'descripcion', 'precio', 'condicion', 'unidad'];
const REQUERIDOS = ['descripcion'];

// Sinónimos ya normalizados (sin acentos, minúsculas). Se comparan contra el
// texto de la celda sin puntuación final.
const SINONIMOS = {
  destinatario: ['destinatario', 'destinatarios', 'nombre', 'nombres', 'cliente', 'para', 'recibe', 'receptor', 'persona', 'destino', 'consignatario', 'nombre destinatario', 'nombre del destinatario'],
  cantidad: ['cantidad', 'cantidades', 'cant', 'cant.', 'qty', 'cnt', 'q', 'no', 'no.', 'num', 'numero'],
  descripcion: ['descripcion', 'descripciones', 'articulo', 'articulos', 'producto', 'productos', 'detalle', 'item', 'items', 'contenido', 'mercancia', 'mercaderia', 'descripcion del articulo', 'desc', 'desc.'],
  precio: ['precio', 'precios', 'valor', 'valor unitario', 'valor unit', 'valor unit.', 'v. unit', 'v.unit', 'unitario', 'fob', 'usd', '$', 'costo', 'precio unitario', 'p. unit', 'p.unit', 'valor $'],
  condicion: ['condicion', 'estado', 'nuevo/usado', 'nuevo usado', 'n/u', 'nu', 'cond', 'cond.', 'condicion (n/u)'],
  unidad: ['unidad', 'unidades', 'unidad de medida', 'u/m', 'um', 'u.m.', 'medida', 'tipo', 'tipo de unidad'],
};

const campoDeEncabezado = (celda) => {
  const txt = normalizar(celda).replace(/[:]+$/, '').trim();
  if (!txt) return null;
  for (const campo of CAMPOS) {
    if (SINONIMOS[campo].includes(txt)) return campo;
  }
  // Coincidencia por prefijo de palabra para variantes ("Cantidad (pzas)").
  for (const campo of CAMPOS) {
    if (SINONIMOS[campo].some((s) => s.length >= 4 && txt.startsWith(s))) return campo;
  }
  return null;
};

const celda = (fila, i) => (i === null || i === undefined || i < 0 ? '' : String((fila || [])[i] ?? '').trim());
const filaVacia = (fila) => (fila || []).every((c) => String(c ?? '').trim() === '');

// ── Lectura del libro ────────────────────────────────────────────────────────
export function hojasDesdeWorkbook(workbook) {
  const nombres = workbook?.SheetNames || [];
  return nombres.map((nombre) => {
    const hoja = workbook.Sheets[nombre];
    const filasRaw = XLSX.utils.sheet_to_json(hoja, { header: 1, defval: '', blankrows: true, raw: true });
    const filas = filasRaw.map((f) => (Array.isArray(f) ? f.map((c) => (c === null || c === undefined ? '' : String(c))) : []));
    const merges = Array.isArray(hoja?.['!merges']) ? hoja['!merges'] : [];
    merges.forEach((m) => {
      const valor = (filas[m.s.r] || [])[m.s.c] ?? '';
      for (let r = m.s.r; r <= m.e.r; r++) {
        if (!filas[r]) filas[r] = [];
        for (let c = m.s.c; c <= m.e.c; c++) {
          if (String(filas[r][c] ?? '').trim() === '') filas[r][c] = valor;
        }
      }
    });
    // Rectangular: todas las filas al mismo ancho.
    const ancho = filas.reduce((w, f) => Math.max(w, f.length), 0);
    return { nombre, filas: filas.map((f) => Array.from({ length: ancho }, (_, i) => f[i] ?? '')) };
  });
}

export function hojasDesdeArrayBuffer(buf) {
  const wb = XLSX.read(buf, { type: 'array' });
  return hojasDesdeWorkbook(wb);
}

// ── Detección de columnas ────────────────────────────────────────────────────
export function detectarColumnas(filas) {
  const lista = Array.isArray(filas) ? filas : [];
  const vacias = { destinatario: null, cantidad: null, descripcion: null, precio: null, condicion: null, unidad: null };
  for (let r = 0; r < lista.length; r++) {
    const fila = lista[r] || [];
    const columnas = { ...vacias };
    let reconocidas = 0;
    fila.forEach((c, i) => {
      const campo = campoDeEncabezado(c);
      if (campo && columnas[campo] === null) {
        columnas[campo] = i;
        reconocidas += 1;
      }
    });
    if (reconocidas >= 2) {
      const faltantes = REQUERIDOS.filter((k) => columnas[k] === null);
      return { headerRowIndex: r, columnas, encabezados: fila.map((c) => String(c ?? '').trim()), faltantes };
    }
  }
  const primeraNoVacia = lista.find((f) => !filaVacia(f)) || [];
  return {
    headerRowIndex: -1,
    columnas: { ...vacias },
    encabezados: primeraNoVacia.map((c) => String(c ?? '').trim()),
    faltantes: [...REQUERIDOS],
  };
}

// ── Celdas ───────────────────────────────────────────────────────────────────
const parseNumero = (txt) => {
  const s = String(txt ?? '').trim();
  if (!s) return null;
  const m = s.match(/^-?\d+(?:[.,]\d+)?/);
  if (!m) return null;
  const n = Number(m[0].replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

export const parsePrecio = (txt) => {
  let s = String(txt ?? '').trim();
  if (!s) return null;
  s = s.replace(/usd|us\$|\$|dolares|dólares/gi, '').replace(/\s+/g, '');
  if (!s) return null;
  if (s.includes(',') && s.includes('.')) {
    s = s.replace(/,/g, '');
  } else if (s.includes(',')) {
    // '12,50' → decimal; '1,250' → miles
    s = /,\d{1,2}$/.test(s) ? s.replace(',', '.') : s.replace(/,/g, '');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const UNIDAD_MAP = [
  [/^(kgs?|kilos?|kilogramos?)\.?$/, 'KG'],
  [/^(lbs?|libras?)\.?$/, 'LB'],
  [/^(par|pares)\.?$/, 'PAR'],
  [/^(juegos?)\.?$/, 'JUEGO'],
  [/^(docenas?)\.?$/, 'DOCENA'],
  [/^(unidad|unidades|und|uds?|pzas?|piezas?|u)\.?$/, 'UNIDAD'],
];

const normalizarUnidadCelda = (txt) => {
  const n = normalizar(txt);
  if (!n) return null;
  const hit = UNIDAD_MAP.find(([re]) => re.test(n));
  // Desconocida: se conserva el texto crudo para que el mapeador la flaguee.
  return hit ? hit[1] : String(txt).trim();
};

// "Ropa usada 5 lbs" → { descripcion: 'Ropa usada', qty: 5, unidad: 'LB' }
const separarPesoFinal = (desc) => {
  const m = String(desc).match(/^(.*?)\s*[-–(]?\s*(\d+(?:[.,]\d+)?)\s*(lbs?|libras?|kgs?|kilos?)\.?\)?\s*$/i);
  if (!m || !m[1].trim()) return null;
  const unidad = /^(k)/i.test(m[3]) ? 'KG' : 'LB';
  return { descripcion: m[1].trim(), qty: Number(m[2].replace(',', '.')), unidad };
};

const pareceNombre = (txt) => {
  const toks = normalizar(txt).split(/\s+/).filter(Boolean);
  if (toks.length === 0 || toks.length > 5) return false;
  return toks.every((tk) => /^[a-zñ.]+$/.test(tk));
};

// Fila con UNA sola celda con texto, que parece nombre y no es un número.
const textoUnico = (fila) => (fila || []).map((c) => String(c ?? '').trim()).filter(Boolean)[0] || '';
const esFilaSoloNombre = (fila) => {
  const noVacias = (fila || []).map((c) => String(c ?? '').trim()).filter(Boolean);
  if (noVacias.length !== 1) return false;
  return parseNumero(noVacias[0]) === null && pareceNombre(noVacias[0]);
};

const CONDICION_USADO_RE = /^(usad[oa]s?|u|second|segunda)$/;

// ── Parser de una hoja ───────────────────────────────────────────────────────
export function parseListaExcel(filas, { mapeo = null, nombreHoja = '' } = {}) {
  const lista = (Array.isArray(filas) ? filas : []).map((f) => (Array.isArray(f) ? f : []));
  const det = detectarColumnas(lista);
  const columnas = { ...det.columnas };
  let headerRowIndex = det.headerRowIndex;
  if (mapeo) {
    CAMPOS.forEach((k) => {
      if (mapeo[k] !== undefined) columnas[k] = mapeo[k] === '' ? null : mapeo[k];
    });
    if (mapeo.headerRowIndex !== undefined && mapeo.headerRowIndex !== null) headerRowIndex = mapeo.headerRowIndex;
  }
  const faltantes = REQUERIDOS.filter((k) => columnas[k] === null || columnas[k] === undefined);
  const etiquetaHoja = nombreHoja ? `Hoja "${nombreHoja}"` : 'Hoja';
  const base = {
    requires_review: true,
    cached: false,
    content_hash: null,
    source: 'excel',
    nombreHoja,
    encabezados: det.encabezados,
    columnas,
    headerRowIndex,
    filasMuestra: lista.filter((f) => !filaVacia(f)).slice(0, 5),
  };

  if (faltantes.length > 0) {
    return {
      ...base,
      extracted: {
        paquetes: [],
        extraction_notes: `${etiquetaHoja}: no se reconoció la columna de ${faltantes.join(', ')} — indicá qué columna es cada campo.`,
        requires_review: true,
      },
      necesitaMapeo: true,
      faltantes,
    };
  }

  const notas = [];
  if (headerRowIndex > 0) {
    const titulos = lista.slice(0, headerRowIndex).filter((f) => !filaVacia(f)).map((f) => f.filter((c) => String(c ?? '').trim()).join(' '));
    if (titulos.length > 0) notas.push(`${etiquetaHoja} — título ignorado: ${titulos.join(' / ')}`);
  }

  const tieneDest = columnas.destinatario !== null && columnas.destinatario !== undefined;
  const paquetes = [];
  let actual = null;
  let cortarGrupo = true; // sin destinatario: la primera fila abre grupo
  const stats = { filas: 0, items: 0, grupos: 0, ambiguas: 0, vacias: 0 };

  const abrirGrupo = (nombre, flag = false) => {
    actual = { owner_name_raw: nombre, requires_review: flag, items: [] };
    paquetes.push(actual);
    stats.grupos += 1;
    return actual;
  };
  const asegurar = () => {
    if (actual) return actual;
    notas.push(`${etiquetaHoja}: hay artículos antes del primer nombre — quedaron en un paquete sin nombre`);
    return abrirGrupo('', true);
  };

  for (let r = headerRowIndex + 1; r < lista.length; r++) {
    const fila = lista[r];
    stats.filas += 1;
    if (filaVacia(fila)) {
      stats.vacias += 1;
      if (!tieneDest) cortarGrupo = true;
      continue;
    }
    const dest = celda(fila, columnas.destinatario);
    const cantTxt = celda(fila, columnas.cantidad);
    let descTxt = celda(fila, columnas.descripcion);
    const precioTxt = celda(fila, columnas.precio);
    const condTxt = celda(fila, columnas.condicion);
    const unidadTxt = celda(fila, columnas.unidad);

    if (tieneDest) {
      if (dest && (!actual || normalizar(actual.owner_name_raw) !== normalizar(dest))) {
        abrirGrupo(dest);
      }
      if (dest && !descTxt && !cantTxt && !precioTxt) continue; // fila solo-nombre
    } else if (esFilaSoloNombre(fila)) {
      // Fila "solo nombre" (una única celda con texto de persona, en cualquier
      // columna): abre grupo. Dos nombres seguidos ⇒ el segundo va flagueado
      // (¿persona o artículo?).
      const nombre = textoUnico(fila);
      const consecutivo = Boolean(actual && actual.items.length === 0 && !cortarGrupo);
      abrirGrupo(nombre, consecutivo);
      if (consecutivo) notas.push(`Revisar: "${nombre}" viene justo después de otro nombre (¿artículo o persona?)`);
      cortarGrupo = false;
      continue;
    } else if (cortarGrupo && actual && actual.items.length > 0) {
      // Fila en blanco previa sin nombre nuevo: grupo sin nombre.
      abrirGrupo('', true);
      notas.push(`${etiquetaHoja}: grupo sin nombre tras una fila en blanco (fila ${r + 1})`);
    }
    cortarGrupo = false;

    const grupo = asegurar();
    let requiresReview = false;
    const notasItem = [];

    if (!descTxt) {
      // Inclasificable: se conserva todo lo que trae, flagueada.
      const resto = fila.filter((c) => String(c ?? '').trim()).join(' · ');
      stats.ambiguas += 1;
      grupo.items.push({
        description_raw: resto,
        qty: 1,
        unit_type: 'UNIDAD',
        unit_price: null,
        is_new: true,
        notes: `Fila ${r + 1} sin descripción reconocible: revisá si es artículo`,
        requires_review: true,
      });
      continue;
    }

    let qty = parseNumero(cantTxt);
    if (cantTxt && qty === null) {
      requiresReview = true;
      notasItem.push(`Cantidad no reconocida: "${cantTxt}"`);
    }
    let unidad = normalizarUnidadCelda(unidadTxt);
    if (!unidad && cantTxt) {
      // "5 lbs" en la celda de cantidad
      const u = cantTxt.replace(/^-?\d+(?:[.,]\d+)?\s*/, '');
      if (u) unidad = normalizarUnidadCelda(u);
    }
    if (qty === null && !unidad) {
      const peso = separarPesoFinal(descTxt);
      if (peso) {
        descTxt = peso.descripcion;
        qty = peso.qty;
        unidad = peso.unidad;
      }
    } else if (!unidad) {
      const peso = separarPesoFinal(descTxt);
      if (peso && qty === peso.qty) {
        descTxt = peso.descripcion;
        unidad = peso.unidad;
      }
    }
    if (qty === null) qty = 1;

    const precio = parsePrecio(precioTxt);
    if (precioTxt && precio === null) {
      requiresReview = true;
      notasItem.push(`Precio no reconocido: "${precioTxt}"`);
    }

    let isNew = true;
    if (condTxt) {
      isNew = !CONDICION_USADO_RE.test(normalizar(condTxt));
    } else if (normalizar(descTxt).split(/\s+/).some((tk) => /^usad[oa]s?$/.test(tk))) {
      isNew = false;
    }

    stats.items += 1;
    grupo.items.push({
      description_raw: descTxt,
      qty,
      unit_type: unidad || 'UNIDAD',
      unit_price: precio,
      is_new: isNew,
      notes: notasItem.length > 0 ? notasItem.join(' · ') : null,
      requires_review: requiresReview,
    });
  }

  if (headerRowIndex === -1 && mapeo) {
    notas.push(`${etiquetaHoja}: sin fila de encabezado — la primera fila se trató como datos`);
  }

  return {
    ...base,
    extracted: {
      paquetes,
      extraction_notes: notas.join(' · '),
      requires_review: true,
    },
    necesitaMapeo: false,
    faltantes: [],
    stats,
  };
}

// ── Varias hojas ─────────────────────────────────────────────────────────────
export function parseListaExcelMultiHoja(hojas, { mapeos = {} } = {}) {
  const lista = Array.isArray(hojas) ? hojas : [];
  const porHoja = lista.map((h, i) => {
    const mapeo = mapeos[h.nombre] ?? mapeos[i] ?? null;
    return { nombre: h.nombre, resultado: parseListaExcel(h.filas, { mapeo, nombreHoja: h.nombre }) };
  });
  const necesitaMapeo = porHoja.some((p) => p.resultado.necesitaMapeo);
  const paquetes = porHoja.flatMap((p) => p.resultado.extracted.paquetes);
  const notas = porHoja.map((p) => p.resultado.extracted.extraction_notes).filter(Boolean);
  return {
    porHoja,
    necesitaMapeo,
    hojasPendientes: porHoja.filter((p) => p.resultado.necesitaMapeo).map((p) => p.nombre),
    resultado: {
      extracted: { paquetes, extraction_notes: notas.join(' · '), requires_review: true },
      requires_review: true,
      cached: false,
      content_hash: null,
      source: 'excel',
      necesitaMapeo,
    },
  };
}

export default parseListaExcel;
